import * as React from 'react';
import { Control, Errors, LocalForm } from 'react-redux-form';

import { CheckboxInline } from '@common/components/checkbox-inline/checkbox-inline';
import { parsePhoneNumber } from '@common/parsers';
import { getDefaultErrorMessages, hasValue, isValidPhoneNumber } from '@common/validators';
import { AutoAuthoViewModel, ConfirmationRequestViewModel, ConfirmationViewModel } from '@services/AutoAuthoService';

import { AutoAuthoAuthorization } from './authorization.component';
import { AutoAuthoAuthorizationConfirmed } from './authorization-confirmed.component';
import { AutoAuthoAuthorizationDenied } from './authorization-denied.component';
import { AutoAuthoStatusUpdate } from './status-update/_components/status-update.container';

import { Link } from 'react-router-dom';
import { RouteDefinitions } from '@common/route-definitions';

export enum AutoAuthoPage {
  AuthorizationConfirmed,
  AuthorizationDenied,
  Authorize,
  Landing,
  UpdateStatus,
  Validation
}

export interface AutoAuthoResult extends ConfirmationViewModel {
  readonly authorizationAmount: number;
}

// Defining the properties the component expects
// Consider this the "setup" of the component
// All properties should be readonly
export interface AutoAuthoComponentProperties {
  readonly authorizationResult?: AutoAuthoResult;
  readonly authorize: (phoneNumber: string, swoId: number, request: ConfirmationRequestViewModel) => void;
  readonly hasConfirmationFailed: boolean;
  readonly getViewModel: (phoneNumber: string, swoId: number, rememberPhoneNumber: boolean) => void;
  readonly getSwoStatusMap: (phoneNumber: string, swoId: number) => void;
  readonly gotoPage: (page: AutoAuthoPage) => void;
  readonly page: AutoAuthoPage;
  readonly phoneNumber?: string;
  readonly swoId?: number;
  readonly swoTripStatusMap?: object;
  readonly updateViewModel: (viewModel: AutoAuthoViewModel) => void;
  readonly viewModel?: AutoAuthoViewModel;
}

// Component definition
export class AutoAuthoComponent extends React.Component<AutoAuthoComponentProperties> {
  private savedPhoneNumber?: string;
  private updateSwoStatusDispatch: Function;

  public componentWillMount(): void {
    this.savedPhoneNumber = localStorage.getItem('autoAuthoPhoneNumber') || undefined;
  }

  public componentDidUpdate(): void {
    if (this.props.phoneNumber && this.props.swoId) {
      if (!this.props.swoTripStatusMap) {
        this.props.getSwoStatusMap(this.props.phoneNumber, this.props.swoId);
      }
    }
  }

  public render(): JSX.Element {
    if (this.props.hasConfirmationFailed) {
      return (
        <div className="row center-xs">
          <div className="col">
            <h2>Review Eligibility</h2>
            <h4>SWO #: {this.props.swoId!}</h4>
            {this.props.viewModel &&
              this.props.viewModel.tripStatus && (
                <h4>SWO Trip Status: {this.getTripStatusDescription(this.props.viewModel.tripStatus)}</h4>
              )}
            <br />
            <h4 className="error">There was an error processing your request. Please try again later.</h4>
          </div>
        </div>
      );
    }

    let content: JSX.Element = <></>;
    switch (this.props.page) {
      case AutoAuthoPage.AuthorizationConfirmed:
        content = (
          <AutoAuthoAuthorizationConfirmed
            isTripStatusUpdateEnabled={this.isTripStatusUpdateEnabled()}
            swoId={this.props.swoId!}
            vendorEmailAddress={this.props.viewModel!.emailAddress!}
            tripStatusDescription={this.getTripStatusDescription(this.props.viewModel!.tripStatus!)}
            result={this.props.authorizationResult!}
            gotoUpdateStatusPage={() => this.props.gotoPage(AutoAuthoPage.UpdateStatus)}
            gotoValidationPage={() => this.props.gotoPage(AutoAuthoPage.Validation)}
          />
        );
        break;
      case AutoAuthoPage.AuthorizationDenied:
        content = (
          <AutoAuthoAuthorizationDenied
            swoId={this.props.swoId!}
            result={this.props.authorizationResult!}
            tripStatusDescription={this.getTripStatusDescription(this.props.viewModel!.tripStatus!)}
            gotoValidationPage={() => this.props.gotoPage(AutoAuthoPage.Validation)}
          />
        );
        break;
      case AutoAuthoPage.Authorize:
        content = (
          <AutoAuthoAuthorization
            authorize={this.props.authorize}
            cancel={() => this.props.gotoPage(AutoAuthoPage.Landing)}
            phoneNumber={this.props.phoneNumber!}
            swoId={this.props.swoId!}
            tripStatusDescription={this.getTripStatusDescription(this.props.viewModel!.tripStatus!)}
          />
        );
        break;
      case AutoAuthoPage.Landing:
        content = this.renderLanding(this.props.viewModel!, this.props.swoId!);
        break;
      case AutoAuthoPage.UpdateStatus:
        content = (
          <AutoAuthoStatusUpdate
            cancel={() => this.props.gotoPage(AutoAuthoPage.Landing)}
            phoneNumber={this.props.phoneNumber!}
            swoId={this.props.swoId!}
            swoTripStatusMap={this.props.swoTripStatusMap}
            updateViewModel={this.props.updateViewModel}
            viewModel={this.props.viewModel!}
          />
        );
        break;
      case AutoAuthoPage.Validation:
      default:
        content = this.renderValidation();
        break;
    }

    return content;
  }

  private getTripStatusDescription(statusId?: number): string {
    if (!this.props.swoTripStatusMap || !statusId) {
      return 'Appointment Pending';
    }
    return this.props.swoTripStatusMap![statusId.toString()];
  }

  private isTripStatusUpdateEnabled(): boolean {
    if (!this.props.viewModel || this.props.viewModel!.vendorIsEligibleForStatusUpdate !== true) {
      return false;
    }

    const tripStatus: number | null | undefined = this.props.viewModel!.tripStatus;
    if (tripStatus == null) {
      return false;
    }
    return [0, 1, 3, 21, 35, 36].includes(tripStatus);
  }

  private renderLanding(viewModel: AutoAuthoViewModel, swoId: number): JSX.Element {
    const isEligibileForAutoAuthorization: boolean =
      (viewModel.swoIsCheckAndAdvise === undefined || viewModel.swoIsCheckAndAdvise === false) &&
      viewModel.swoIsEligible === true &&
      viewModel.swoTypeIsEligible === true &&
      viewModel.vendorIsEligibleForAutoAutho === true;

    return (
      <div className="row center-xs">
        <div className="col">
          <h2>Task Selection</h2>
          <h4>SWO #: {swoId}</h4>
          <h4>SWO Trip Status: {this.getTripStatusDescription(viewModel.tripStatus!)}</h4>
          {!isEligibileForAutoAuthorization && (
            <>
              <h4>SWO is ineligible for the following reasons:</h4>
              <ul className="list-unstyled">
                {viewModel.swoIsCheckAndAdvise === true && (
                  <li>
                    <strong>Check and Advise</strong>
                  </li>
                )}
                {viewModel.swoIsEligible === false && (
                  <li>
                    <strong>Ineligible SWO</strong>
                  </li>
                )}
                {viewModel.swoTypeIsEligible === false && (
                  <li>
                    <strong>Ineligible SWO Type</strong>
                  </li>
                )}
                {viewModel.vendorIsEligibleForAutoAutho === false && (
                  <li>
                    <strong>Ineligible Vendor</strong>
                  </li>
                )}
              </ul>
            </>
          )}
          <p>Please make a selection below.</p>
          <button
            className="btn btn-primary"
            disabled={!isEligibileForAutoAuthorization}
            onClick={() => this.props.gotoPage(AutoAuthoPage.Authorize)}
          >
            Authorize
          </button>
          <br />
          <br />
          <button
            className="btn btn-primary"
            disabled={!this.isTripStatusUpdateEnabled()}
            onClick={() => this.props.gotoPage(AutoAuthoPage.UpdateStatus)}
          >
            Update SWO Status
          </button>
          <br />
          <br />
          <button className="btn btn-primary" onClick={() => this.props.gotoPage(AutoAuthoPage.Validation)}>
            Change SWO
          </button>
          <br />
          <br />
          <div className="row center-xs">
            <div className="col">
              <Link to={RouteDefinitions.tutorial.AutoAuthoTutorial} target="_blank">
                <h4>Auto-Authorization Tutorial</h4>
              </Link>
            </div>
            <div className="col">
              <Link to={RouteDefinitions.tutorial.SWOStatusTutorial} target="_blank">
                <h4>SWO Status Tutorial</h4>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  private renderValidation(): JSX.Element {
    return (
      <div className="row center-xs">
        <LocalForm
          className="col"
          hideNativeErrors={true}
          initialState={{
            phoneNumber: this.savedPhoneNumber || '',
            rememberPhoneNumber: this.savedPhoneNumber && this.savedPhoneNumber !== ''
          }}
          onSubmit={m => this.props.getViewModel(m.phoneNumber, m.swoId, m.rememberPhoneNumber)}
          getDispatch={d => (this.updateSwoStatusDispatch = d)}
        >
          <h2>Confirm SWO</h2>
          <p>Please enter your Vendor Phone Number and SWO Number to validate your access.</p>
          <br />
          <div className="row center-xs">
            <div className="form-control col-md-7 col-xs-11">
              <label>Office Phone Number</label>
              <Control.text
                model=".phoneNumber"
                parser={parsePhoneNumber}
                validators={{ required: hasValue, isPhone: isValidPhoneNumber }}
              />
              <Errors
                className="error"
                model=".phoneNumber"
                show={{ touched: true, focus: false }}
                messages={getDefaultErrorMessages()}
              />
            </div>
          </div>
          <div className="row center-xs">
            <div className="form-control col-md-7 col-xs-11">
              <label>SWO #</label>
              <Control.text
                model=".swoId"
                parser={v => v && parseInt(v, 10)}
                type="number"
                validators={{ required: hasValue }}
              />
              <Errors
                className="error"
                model=".swoId"
                show={{ touched: true, focus: false }}
                messages={getDefaultErrorMessages()}
              />
            </div>
          </div>
          <div className="row center-xs">
            <div className="form-control col-md-7 col-xs-11">
              <CheckboxInline>
                <Control.checkbox model=".rememberPhoneNumber" />
                <label>Remember Phone Number</label>
              </CheckboxInline>
            </div>
          </div>
          <button className="btn btn-primary" type="submit">
            Submit
          </button>
        </LocalForm>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/auto-autho/_components/auto-autho.component.tsx